<template>
  <div>
    <div v-if="step == 0">
      <app-step0 @nextStep="nextStep" />
    </div>
    <div v-else-if="step == 1">
      <app-step1 :cycles="cycles" @nextStep="nextStep" :tableText="tableText" />
    </div>
    <div v-else-if="step == 2">
      <app-step2 :items="recipients" @nextStep="nextStep" :pollType="this.selectedCycle.Type2" />
    </div>
    <div v-else-if="step == 3">
      <app-step3 :cycle="selectedCycle" :recipient="selectedRecipient" :questions="questions" @nextStep="nextStep" />
    </div>
  </div>
</template>

<script>
import Step0 from "./Step0";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";

import { getCycles, getRecipients, getPoll, savePoll } from "@/api/poll";
export default {
  data() {
    return {
      step: 0,
      pollFetched: false,
      recipientFetched: false,
      selectedCycle: null,
      selectedRecipient: null,
      cycles: [],
      recipients: [],
      questions: [],
      questionsFetched: false,
    };
  },
  components: {
    appStep0: Step0,
    appStep1: Step1,
    appStep2: Step2,
    appStep3: Step3,
  },
  watch: {
    // Watch for route changes to handle browser back/forward buttons
    '$route.query': {
      handler(newQuery) {
        this.handleRouteChange(newQuery);
      },
      // immediate: true
    },

  },
  created() {
    if (this.getCurrentStepFromQuery() > 0) {
      this.$router.replace({ query: {} });
    }
    this.getCycles();
  },
  methods: {
    // Query parameter methods
    getCurrentStepFromQuery() {
      const stepParam = this.$route.query.step;
      return stepParam ? parseInt(stepParam) : 0;
    },

    updateUrlWithStep(newStep, additionalParams = {}) {
      if (newStep === 0) {
        // If we're at step 0, remove query params
        this.$router.push({ query: {} });
      } else {
        // Update step and preserve other data
        this.$router.push({
          query: {
            step: newStep,
            ...additionalParams
          }
        });
      }
    },

    handleRouteChange(queryParams) {
      const newStep = this.getCurrentStepFromQuery();
      const previousStep = this.step;

      if (newStep !== this.step) {
        this.step = newStep;
        window.scrollTo(0, 0);
        this.handleStepTransition(previousStep, newStep);
      }
    },

    handleStepTransition(previousStep, newStep) {
      // Handle data reset when going back
      if (newStep < previousStep) {
        if (newStep === 1 && previousStep >= 2) {
          // Going back from step 2 or 3 to step 1 - reset recipient data
          this.recipientFetched = false;
          this.questionsFetched = false;
          this.selectedRecipient = null;
          this.questions = [];
        } else if (newStep === 0 && previousStep >= 1) {
          // Going back to step 0 - reset everything
          this.selectedCycle = null;
          this.selectedRecipient = null;
          this.recipients = [];
          this.questions = [];
          this.recipientFetched = false;
          this.questionsFetched = false;
        }
      }
    },

    nextStep(param) {
      switch (this.step) {
        case 0:
          if (!this.pollFetched) {
            this.getCycles();
          } else {
            if (this.cycles.length == 0) {
              this.$store.commit(
                "SET_SNACKBAR_TEXT",
                "در این بازه تاریخی نظرسنجی فعالی وجود ندارد."
              );

              return;
            }
          }
          break;
        case 1:
          this.selectedCycle = param;
          if (!this.recipientFetched) {
            this.getRecipients(
              this.selectedCycle.FSerial,
              this.selectedCycle.Type2
            );
          }

          break;
        case 2:
          this.selectedRecipient = param;
          if (!this.questionsFetched) {
            this.getPoll(this.selectedCycle.FSerial);
          }

          break;

        case 3:
          this.$store.commit("SET_API_RUNNING", true);
          savePoll(
            this.selectedCycle.FSerial,
            this.selectedRecipient.Fserial,
            this.selectedCycle.Type2,
            this.selectedRecipient.Srl_School,
            this.selectedCycle.Type2 == 3
              ? this.selectedRecipient.Srl_TeacherTitle
              : null,
            this.selectedCycle.Type2 != 3
              ? this.selectedRecipient.Srl_Chart
              : null,
            param
          )
            .then((response) => {
              const data = response.data;
              this.$store.commit("SET_SNACKBAR_TEXT", data.Message);
              this.$store.commit("SET_API_RUNNING", false);
              if (data.Success) {
                // Go back to step 2 using query params
                this.updateUrlWithStep(2);
                this.getRecipients(
                  this.selectedCycle.FSerial,
                  this.selectedCycle.Type2
                );
              }
            })
            .catch((error) => {
              this.$store.commit(
                "SET_SNACKBAR_TEXT",
                "خطایی به وجود آمده است لطفا مجددا تلاش کنید."
              );
            });
          return; // Don't increment step here as we handle it in the response
      }

      if (this.step < 3) {
        const newStep = this.step + 1;

        // Add additional parameters based on current step
        let additionalParams = {};
        if (this.step === 1) {
          additionalParams.cycle = this.selectedCycle?.FSerial || '';
        } else if (this.step === 2) {
          additionalParams = {
            cycle: this.selectedCycle?.FSerial || '',
            recipient: this.selectedRecipient?.Fserial || ''
          };
        }

        this.updateUrlWithStep(newStep, additionalParams);
      }
    },

    getCycles() {
      this.$store.commit("SET_TABLE_TEXT", true);

      getCycles()
        .then((response) => {
          const data = response.data;
          this.cycles = data;
          this.pollFetched = true;
          this.$store.commit("SET_TABLE_TEXT", false);
          if (this.cycles.length == 0) {
            this.$store.commit(
              "SET_SNACKBAR_TEXT",
              "در این بازه تاریخی نظرسنجی فعالی وجود ندارد."
            );
          }
        })
        .catch((error) => {
          this.$store.commit(
            "SET_SNACKBAR_TEXT",
            "خطایی به وجود آمده است لطفا مجددا تلاش کنید."
          );
        });
    },
    getRecipients(FSerial, Type2) {
      getRecipients(FSerial, Type2)
        .then((response) => {
          const data = response.data;
          this.recipients = data;
          this.recipientFetched = true;
          this.$store.commit("SET_TABLE_TEXT", false);
        })
        .catch((error) => {
          this.$store.commit(
            "SET_SNACKBAR_TEXT",
            "خطایی به وجود آمده است لطفا مجددا تلاش کنید."
          );
        });
    },
    getPoll(FSerial) {
      getPoll(FSerial)
        .then((response) => {
          const data = response.data;
          this.questions = data;
          this.questionsFetched = true;
        })
        .catch((error) => {
          this.$store.commit(
            "SET_SNACKBAR_TEXT",
            "خطایی به وجود آمده است لطفا مجددا تلاش کنید."
          );
        });
    },
  },

  computed: {
    isMobile() {
      return this.$store.getters.isMobile;
    },

    tableText() {
      return this.$store.getters.tableText;
    },
    apiRunning() {
      return this.$store.getters.apiRunning;
    },
  },
};
</script>